import { ref, readonly, computed } from 'vue';
import { OPERATORS, DIGITS } from '@/shared/constants';
import { AnswerFormattedComputation, ComputationMeta } from '@/types/TaskConfig';
import { evaluate, format } from 'mathjs';

const clearOnNextDigit = ref(false);
const fullMemory = ref('');
const memory = ref('');
const error = ref(false);
// Multiple
const fullMemoryArray = ref<string[]>([]);
const fullMemoryMetaArray = ref<ComputationMeta[]>([]);
const deletedFromFullMemory = ref('');
const deletedMetaFromFullMemory = ref<ComputationMeta | null>(null);

const splitRegex = new RegExp(`[\\${OPERATORS.join('\\')}]`, 'g');
const memoryFirstNumber = computed(() => (memory.value ? memory.value.split(splitRegex)[0] : null));
const memorySecondNumber = computed(() => (memory.value ? memory.value.split(splitRegex)[1] : null));
const memoryOperator = computed(() => {
    if (!memory.value || !memoryFirstNumber.value) return null;
    const match = memory.value.match(new RegExp(`[${OPERATORS.join('')}]`));
    return match ? match[0] : null;
});

const computationFormattedForServer = computed<AnswerFormattedComputation>(() => {
    return {
        firstNumber: memoryFirstNumber.value && +memoryFirstNumber.value !== null ? +memoryFirstNumber.value : null,
        secondNumber: memorySecondNumber.value && +memorySecondNumber.value !== null ? +memorySecondNumber.value : null,
        operator: memoryOperator.value || null,
    };
});

function useCalculate() {
    const calculatorErrorMessage = ref('');
    const setCalculatorErrorMessage = (message: string) => {
        calculatorErrorMessage.value = message;
    };
    const clearCalculatorErrorMessage = () => {
        calculatorErrorMessage.value = '';
    };

    function isOperator(string: string) {
        return OPERATORS.includes(string);
    }

    function isDigit(string: string) {
        return DIGITS.includes(string);
    }

    function lastCharIsOperator(string: string) {
        const stringNormalized = string.replace(/\s/g, '');
        return isOperator(stringNormalized[stringNormalized.length - 1]);
    }

    function eraseLast() {
        if (!memory.value.length) return;

        memory.value = memory.value.slice(0, memory.value.length - 1);
        clearOnNextDigit.value = false;
    }

    function clear() {
        memory.value = '';
        fullMemory.value = '';
        error.value = false;
    }

    function reOrderFullMemoryMetaArrayIndexes() {
        fullMemoryMetaArray.value = fullMemoryMetaArray.value.map((m, index) => {
            return {
                ...m,
                index,
            };
        });
    }

    function clearOneFromFullMemoryArray(index: number) {
        clear();

        const deleted = fullMemoryArray.value[index];
        fullMemoryArray.value.splice(index, 1);
        deletedFromFullMemory.value = deleted;

        const deletedMeta = fullMemoryMetaArray.value.find((m) => m.index === index) || null;
        fullMemoryMetaArray.value = fullMemoryMetaArray.value.filter((m) => m.index !== index);
        deletedMetaFromFullMemory.value = deletedMeta;
        reOrderFullMemoryMetaArrayIndexes();
    }

    function addDigit(digit: string) {
        if (!isDigit(digit)) {
            throw new Error('Invalid param, is not a valid digit');
        }

        const lastDigit = memory.value[memory.value.length - 1];

        if ((digit === '0' || digit === '.') && lastDigit === '/') {
            setCalculatorErrorMessage("Il n'est pas possible de faire une division par 0.");
            return;
        }

        if (memory.value.length >= 15) {
            return;
        }

        if (digit === '.') {
            if (!memoryOperator.value && memoryFirstNumber.value?.includes('.')) return;
            if (memoryOperator.value && memorySecondNumber.value?.includes('.')) return;
        }
        if (lastDigit === '0' && memory.value.length === 1) clear();
        if (clearOnNextDigit.value) clear();
        if ((!memory.value || lastCharIsOperator(memory.value)) && digit === '.') memory.value += '0';

        clearOnNextDigit.value = false;
        memory.value += `${digit}`;
    }

    function addOperator(operator: string) {
        if (!isOperator(operator)) {
            throw new Error('Invalid param, is not a valid operator');
        }

        if (memory.value.length >= 15) {
            return;
        }

        if (!memory.value) return;
        if (lastCharIsOperator(memory.value)) return;

        // Prevent adding operator if there is already one
        if (OPERATORS.some((operatorToCheck) => memory.value.includes(operatorToCheck))) {
            return;
        }

        clearOnNextDigit.value = false;
        memory.value += `${operator}`;
    }

    function calculateResult() {
        if (!memory.value) return;

        if (lastCharIsOperator(memory.value)) {
            return;
        }

        if (!OPERATORS.some((operator) => memory.value.includes(operator))) {
            return;
        }

        try {
            const plainData = JSON.parse(JSON.stringify(computationFormattedForServer.value));
            fullMemory.value = '';
            const mathExpression = memory.value.replace(/\b0*((\d+\.\d+|\d+))\b/g, '$1'); // remove octal numeric
            fullMemory.value = `${mathExpression}=`;
            const expressionResult = evaluate(mathExpression);
            const formattedExpressionResult = format(expressionResult, { precision: 14 });
            memory.value = `${formattedExpressionResult}`;
            const memorySplit = memory.value.split('.');

            let newMemory = '';
            if (memorySplit.length > 1 && memorySplit[1].length > 3) {
                newMemory = `${memorySplit[0]},${memorySplit[1][0]}${memorySplit[1][1]}${memorySplit[1][2]}...`;
            } else {
                newMemory = memory.value.replaceAll('.', ',');
            }
            fullMemory.value = fullMemory.value.replaceAll('.', ',');
            fullMemory.value += newMemory;

            // adding all the computations to the memory array
            const index = fullMemoryArray.value.length;
            fullMemoryArray.value.push(fullMemory.value);
            fullMemoryMetaArray.value.push({
                index,
                data: plainData,
            });

            // clear the calculator screen when the result calculus is submitted
            memory.value = '';
        } catch (_) {
            error.value = true;
            memory.value = '';
        } finally {
            clearOnNextDigit.value = true;
        }
    }

    function resetMemory() {
        fullMemory.value = '';
        memory.value = '';
    }

    function resetResults() {
        fullMemoryArray.value = [];
        fullMemoryMetaArray.value = [];
        deletedFromFullMemory.value = '';
        deletedMetaFromFullMemory.value = null;
    }

    function resetCalculator() {
        clearOnNextDigit.value = false;
        resetMemory();
        resetResults();
        error.value = false;
    }

    return {
        memory: readonly(memory),
        error: readonly(error),
        fullMemory: readonly(fullMemory),
        fullMemoryArray: readonly(fullMemoryArray),
        fullMemoryMetaArray: readonly(fullMemoryMetaArray),
        deletedFromFullMemory: readonly(deletedFromFullMemory),
        deletedMetaFromFullMemory: readonly(deletedMetaFromFullMemory),
        calculatorErrorMessage: readonly(calculatorErrorMessage),
        clearCalculatorErrorMessage,
        addDigit,
        addOperator,
        calculateResult,
        eraseLast,
        clear,
        clearOneFromFullMemoryArray,
        resetCalculator,
        resetMemory,
        resetResults,
        computationFormattedForServer,
    };
}

export default useCalculate;
